/**
 * Carte — LA surface qui pose du contenu (lot « surfaces » du catalogue, 01-11-2026).
 *
 * Kayla, 01-11 : « je suis pas censée avoir encore des contours comme ça, des cartes comme ça ».
 * Un verre au grand arrondi, une hairline à peine visible dérivée de l'encre, un reflet en haut, une
 * ombre douce (surfaces.css) — à la place des `rounded border border-line bg-panel` écrits à la main
 * dans quatre cents fichiers. En verre, un voile clair sur le fond d'écran, jamais un bloc noir.
 *
 *   <Carte>…</Carte>                              16 px de marge intérieure
 *   <Carte remplissage="lg" />                    24 px (16 sur un téléphone)
 *   <Carte remplissage="aucun" />                 rien : le contenu gère (une liste, une image)
 *   <Carte onClick={…} actif={choisie} />         un vrai bouton, qui se soulève ; la marque sur la choisie
 *   <Carte href="/x" />                           un vrai lien
 *
 * `Section` est la carte avec sa tête : une étiquette, un sous-titre, des actions ou une note à
 * droite, puis le corps.
 */
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, HTMLAttributes, ReactNode } from "react";

import { Etiquette } from "./Etiquette";
import { cn } from "./utils";
import "./surfaces.css";

export type RemplissageCarte = "aucun" | "sm" | "md" | "lg";

interface CarteBase {
  children?: ReactNode;
  /** La marge intérieure. Défaut `md` (16 px). */
  remplissage?: RemplissageCarte;
  /** La carte choisie : hairline à la marque, halo doux. */
  actif?: boolean;
  className?: string;
}

type CarteDiv = CarteBase & Omit<HTMLAttributes<HTMLDivElement>, "children" | "className" | "onClick"> & { onClick?: undefined; href?: undefined };
type CarteBouton = CarteBase &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children" | "className" | "onClick"> & {
    onClick: ButtonHTMLAttributes<HTMLButtonElement>["onClick"];
    href?: undefined;
  };
type CarteLien = CarteBase & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "children" | "className" | "href"> & { href: string; onClick?: undefined };

export type CarteProps = CarteDiv | CarteBouton | CarteLien;

export function Carte(props: CarteProps) {
  const { children, remplissage = "md", actif = false, className, ...reste } = props;
  const commun = {
    "data-remplissage": remplissage === "aucun" ? undefined : remplissage,
    "data-actif": actif || undefined,
    className: cn("sf-carte sf-arrondi", className),
  };
  if ("href" in reste && reste.href !== undefined) {
    const { href, ...a } = reste as CarteLien;
    return (
      <a href={href} {...a} {...commun} data-cliquable="">
        {children}
      </a>
    );
  }
  if ("onClick" in reste && reste.onClick !== undefined) {
    const b = reste as CarteBouton;
    return (
      <button type="button" {...b} {...commun} data-cliquable="">
        {children}
      </button>
    );
  }
  return (
    <div {...(reste as HTMLAttributes<HTMLDivElement>)} {...commun}>
      {children}
    </div>
  );
}

export interface SectionProps {
  /** L'étiquette de la section (« Prochain shift »), en capitales espacées. */
  titre: ReactNode;
  /** Sous l'étiquette, gris. */
  sousTitre?: ReactNode;
  /** Avant l'étiquette. */
  icone?: ReactNode;
  /** À droite : des boutons, un sélecteur. */
  actions?: ReactNode;
  /** À droite, en petit et gris : « heure de Paris », « 14 derniers jours ». */
  note?: ReactNode;
  /** La marge intérieure du corps. Défaut `md` ; `aucun` pour une liste ou un tableau. */
  remplissage?: "aucun" | "md";
  /** Un trait à peine visible entre la tête et le corps. */
  separe?: boolean;
  children?: ReactNode;
  className?: string;
  /** Sur le corps. */
  classNameCorps?: string;
}

/** Une carte avec sa tête, puis son corps. `<section>`, titrée pour les lecteurs d'écran. */
export function Section({ titre, sousTitre, icone, actions, note, remplissage = "md", separe = false, children, className, classNameCorps }: SectionProps) {
  return (
    <section className={cn("sf-carte sf-arrondi", className)}>
      <div className="sf-tete">
        <div className="sf-tete-titre">
          <Etiquette icone={icone}>{titre}</Etiquette>
          {sousTitre !== undefined && sousTitre !== null && <div className="sf-sous-titre">{sousTitre}</div>}
        </div>
        {note !== undefined && note !== null && <span className="sf-note">{note}</span>}
        {actions && <div className="sf-tete-actions">{actions}</div>}
      </div>
      <div className={cn("sf-corps", separe && "sf-hairline", classNameCorps)} data-remplissage={remplissage === "aucun" ? "aucun" : undefined}>
        {children}
      </div>
    </section>
  );
}
